import { useCallback, useEffect, useRef, useState } from 'react';
import { Modal, ModalOverlay } from 'react-aria-components';
import { ImagePlus, Loader2, Upload } from 'lucide-react';
import {
  Dialog,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogBody,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { imageService } from '@/services/imageService';
import { cn } from '@/lib/utils';
import type { Product, ProductImage } from '@/types/domain';

interface ProductImagesDialogProps {
  product: Product;
  /** Called after an upload so the caller can refresh its own copy of the product. */
  onUploaded?: () => void;
  onClose: () => void;
}

/**
 * Every image attached to one product, with a way to add more without leaving the quote.
 *
 * A quote line carries all of a product's images, so a rep building a quote is exactly the
 * person who notices a product is short of shots. Uploads go through the normal image path
 * (`POST /api/v1/images/upload` with the product's brand and SKU), which links the new row to
 * the product and fills `products.image_url` when it is still empty.
 */
export function ProductImagesDialog({ product, onUploaded, onClose }: ProductImagesDialogProps) {
  const [images, setImages] = useState<ProductImage[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setImages(await imageService.listByProduct(product.id));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load images');
    } finally {
      setLoading(false);
    }
  }, [product.id]);

  useEffect(() => {
    void load();
  }, [load]);

  async function handleFiles(files: FileList | null) {
    if (!files || files.length === 0) return;
    const brand = product.brand_name_cleaned || product.brand;
    if (!brand) {
      setError('This product has no brand, so an image cannot be filed against it');
      return;
    }

    setUploading(true);
    setError(null);
    try {
      for (const file of Array.from(files)) {
        await imageService.upload(file, brand, {
          matched_sku: product.sku,
          sku_confidence: 1,
          original_filename: file.name,
        });
      }
      await load();
      onUploaded?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Upload failed');
    } finally {
      setUploading(false);
      if (fileInput.current) fileInput.current.value = '';
    }
  }

  return (
    <ModalOverlay isDismissable className="fixed inset-0 z-[100] bg-black/60 backdrop-blur-sm">
      <Modal className="fixed inset-0 flex items-center justify-center p-4">
        <Dialog className="w-full max-w-lg rounded-xl border border-border bg-card shadow-xl">
          <DialogHeader>
            <DialogTitle>Images for {product.sku}</DialogTitle>
            <DialogDescription>
              {product.name} — every image here travels with the quote.
            </DialogDescription>
          </DialogHeader>

          <DialogBody className="space-y-3">
            {error && (
              <p className="rounded-md border border-destructive/40 bg-destructive/10 p-2 text-xs text-destructive">
                {error}
              </p>
            )}

            {loading ? (
              <div className="flex justify-center py-8">
                <Loader2 className="size-5 animate-spin text-muted-foreground" />
              </div>
            ) : images.length === 0 ? (
              <p className="py-6 text-center text-sm text-muted-foreground">
                No images attached to this product yet.
              </p>
            ) : (
              <div className="grid grid-cols-3 gap-2 sm:grid-cols-4">
                {images.map((image) => (
                  <figure key={image.id} className="space-y-1">
                    <div className="aspect-square overflow-hidden rounded-md border bg-muted">
                      <img
                        src={image.url}
                        alt={image.filename}
                        loading="lazy"
                        className="size-full object-cover"
                      />
                    </div>
                    <figcaption className="truncate text-[10px] text-muted-foreground">
                      {image.filename}
                    </figcaption>
                  </figure>
                ))}
              </div>
            )}

            <label className="flex cursor-pointer items-center justify-center gap-2 rounded-md border border-dashed p-4 text-sm text-muted-foreground hover:border-primary hover:text-foreground">
              {uploading ? (
                <Loader2 className="size-4 animate-spin" />
              ) : (
                <Upload className="size-4" />
              )}
              {uploading ? 'Uploading…' : 'Add images'}
              <input
                ref={fileInput}
                type="file"
                accept="image/*"
                multiple
                className="sr-only"
                disabled={uploading}
                onChange={(e) => handleFiles(e.target.files)}
              />
            </label>
          </DialogBody>

          <DialogFooter>
            <Button intent="outline" size="sm" onPress={onClose}>
              Done
            </Button>
          </DialogFooter>
        </Dialog>
      </Modal>
    </ModalOverlay>
  );
}

/**
 * Opens the image manager from a product row. A product with no image at all is flagged,
 * since that is the one a rep will want to fix before putting it in front of a customer.
 */
export function ProductImagesButton({ hasImage, onPress }: { hasImage: boolean; onPress: () => void }) {
  return (
    <button
      type="button"
      onClick={onPress}
      title={hasImage ? 'Product images' : 'No image — add one'}
      className={cn(
        'rounded p-1.5 hover:bg-accent hover:text-foreground',
        hasImage ? 'text-muted-foreground' : 'text-amber-600 dark:text-amber-400',
      )}
      aria-label={hasImage ? 'Product images' : 'No image — add one'}
    >
      <ImagePlus className="size-3.5" />
    </button>
  );
}
